import React, { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'
import { formatDateTimeMinute } from '../lib/date'
import { AccountStatusLabel } from '../lib/labels'
import ListContainer from '../ui/ListContainer'
import { useToast } from '../ui/ToastProvider'

export default function SubscriptionsPage() {
    const { show } = useToast()
    const { user } = useAuth()
    const [orders, setOrders] = useState([])
    const [onboardingRequests, setOnboardingRequests] = useState([])
    const [organizations, setOrganizations] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [activeTab, setActiveTab] = useState('orders')

    // 온보딩 검토 상태
    const [selectedOnboarding, setSelectedOnboarding] = useState(null)
    const [showOnboardingModal, setShowOnboardingModal] = useState(false)
    const [reviewComment, setReviewComment] = useState('')

    // 조직 발급 상태
    const [showOrgModal, setShowOrgModal] = useState(false)
    const [orgForm, setOrgForm] = useState({
        name: '',
        plan: 'STANDARD',
        seats: 10,
        expiresAt: '',
        contactEmail: '',
        contactPhone: ''
    })

    // 데이터 로딩
    const loadData = async () => {
        setLoading(true)
        setError(null)
        try {
            const [ordersRes, onboardingRes, orgsRes] = await Promise.all([
                api.get('/subscriptions/orders'),
                api.get('/subscriptions/onboarding'),
                api.get('/subscriptions/organizations')
            ])

            const orders = Array.isArray(ordersRes.data?.data) ? ordersRes.data.data : (ordersRes.data?.items || ordersRes.data || [])
            const onboarding = Array.isArray(onboardingRes.data?.data) ? onboardingRes.data.data : (onboardingRes.data?.items || onboardingRes.data || [])
            const orgs = Array.isArray(orgsRes.data?.data) ? orgsRes.data.data : (orgsRes.data?.items || orgsRes.data || [])
            setOrders(orders)
            setOnboardingRequests(onboarding)
            setOrganizations(orgs)
        } catch (e) {
            setError('데이터를 불러오지 못했습니다')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    // 온보딩 검토 함수들
    const reviewOnboarding = async (onboardingId, approved, comment = '') => {
        try {
            await api.post(`/subscriptions/onboarding/${onboardingId}/review`, { approved, comment, reviewedBy: user.id })
            show(approved ? '온보딩 승인 완료' : '온보딩 거부 완료', 'success')
            setShowOnboardingModal(false)
            setSelectedOnboarding(null)
            setReviewComment('')
            loadData()
        } catch (err) {
            show('온보딩 검토 실패', 'error')
        }
    }

    // 조직 발급 함수
    const issueOrganization = async () => {
        if (!orgForm.name.trim()) return show('조직명을 입력하세요', 'error')
        try {
            await api.post('/subscriptions/organizations', { ...orgForm, seats: Number(orgForm.seats), expiresAt: orgForm.expiresAt ? new Date(orgForm.expiresAt).toISOString() : null })
            show('조직 발급 완료', 'success')
            setOrgForm({ name: '', plan: 'STANDARD', seats: 10, expiresAt: '', contactEmail: '', contactPhone: '' })
            setShowOrgModal(false)
            loadData()
        } catch (err) {
            show('조직 발급 실패', 'error')
        }
    }

    // 조직 상태 변경
    const changeOrgStatus = async (orgId, newStatus) => {
        try {
            await api.patch(`/subscriptions/organizations/${orgId}/status`, { status: newStatus })
            show('조직 상태 변경 완료', 'success')
            loadData()
        } catch (err) {
            show('조직 상태 변경 실패', 'error')
        }
    }

    // 상태별 스타일
    const getStatusStyle = (status) => {
        switch (status) {
            case 'ACTIVE': return 'bg-green-100 text-green-800'
            case 'EXPIRED': return 'bg-red-100 text-red-800'
            case 'SUSPENDED': return 'bg-yellow-100 text-yellow-800'
            case 'TRIAL': return 'bg-blue-100 text-blue-800'
            case 'EVENT': return 'bg-purple-100 text-purple-800'
            case 'PENDING': return 'bg-gray-100 text-gray-800'
            default: return 'bg-gray-100 text-gray-800'
        }
    }

    const OrdersTable = (
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                    <th className="px-4 py-3 text-left">주문번호</th>
                    <th className="px-4 py-3 text-left">구매자</th>
                    <th className="px-4 py-3 text-left">플랜</th>
                    <th className="px-4 py-3 text-left">금액</th>
                    <th className="px-4 py-3 text-left">상태</th>
                    <th className="px-4 py-3 text-left">주문일시</th>
                    <th className="px-4 py-3 text-left">액션</th>
                </tr>
            </thead>
            <tbody>
                {orders.map(order => (
                    <tr key={order.id} className="border-t hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium">{order.id}</td>
                        <td className="px-4 py-3">{order.buyer}</td>
                        <td className="px-4 py-3">{order.plan}</td>
                        <td className="px-4 py-3">{order.amount ? `₩${order.amount.toLocaleString()}` : '-'}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-1 rounded text-xs ${getStatusStyle(order.status)}`}>{AccountStatusLabel[order.status] || order.status}</span></td>
                        <td className="px-4 py-3">{formatDateTimeMinute(order.createdAt)}</td>
                        <td className="px-4 py-3">{order.status === 'COMPLETED' && (<button className="text-brand-navy hover:underline text-sm" onClick={() => { setOrgForm(prev => ({ ...prev, name: `${order.buyer} 조직` })); setShowOrgModal(true) }}>조직 발급</button>)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    )

    const OnboardingTable = (
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                    <th className="px-4 py-3 text-left">신청자</th>
                    <th className="px-4 py-3 text-left">조직명</th>
                    <th className="px-4 py-3 text-left">플랜</th>
                    <th className="px-4 py-3 text-left">상태</th>
                    <th className="px-4 py-3 text-left">신청일시</th>
                    <th className="px-4 py-3 text-left">액션</th>
                </tr>
            </thead>
            <tbody>
                {onboardingRequests.map(request => (
                    <tr key={request.id} className="border-t hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium">{request.applicantName}</td>
                        <td className="px-4 py-3">{request.organizationName}</td>
                        <td className="px-4 py-3">{request.requestedPlan}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-1 rounded text-xs ${getStatusStyle(request.status)}`}>{request.status === 'RECEIVED' ? '검토 대기' : AccountStatusLabel[request.status] || request.status}</span></td>
                        <td className="px-4 py-3">{formatDateTimeMinute(request.createdAt)}</td>
                        <td className="px-4 py-3">{request.status === 'RECEIVED' && (<button className="text-brand-navy hover:underline text-sm" onClick={() => { setSelectedOnboarding(request); setShowOnboardingModal(true) }}>검토</button>)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    )

    const OrgsTable = (
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                    <th className="px-4 py-3 text-left">조직명</th>
                    <th className="px-4 py-3 text-left">플랜</th>
                    <th className="px-4 py-3 text-left">좌석수</th>
                    <th className="px-4 py-3 text-left">상태</th>
                    <th className="px-4 py-3 text-left">만료일</th>
                    <th className="px-4 py-3 text-left">액션</th>
                </tr>
            </thead>
            <tbody>
                {organizations.map(org => (
                    <tr key={org.id} className="border-t hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium">{org.name}</td>
                        <td className="px-4 py-3">{org.plan}</td>
                        <td className="px-4 py-3">{org.seats}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-1 rounded text-xs ${getStatusStyle(org.status)}`}>{AccountStatusLabel[org.status] || org.status}</span></td>
                        <td className="px-4 py-3">{org.expiresAt ? formatDateTimeMinute(org.expiresAt) : '무제한'}</td>
                        <td className="px-4 py-3">
                            <select className="text-sm border rounded px-2 py-1" value={org.status} onChange={(e) => changeOrgStatus(org.id, e.target.value)}>
                                <option value="ACTIVE">ACTIVE</option>
                                <option value="EXPIRED">EXPIRED</option>
                                <option value="SUSPENDED">SUSPENDED</option>
                                <option value="TRIAL">TRIAL</option>
                                <option value="EVENT">EVENT</option>
                            </select>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )

    return (
        <section>
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">구독 관리</h1>
                    <p className="text-slate-600 mt-2">외부 주문/온보딩 검토/조직 발급/상태 관리</p>
                </div>
            </div>

            {/* 탭 네비게이션 */}
            <div className="mt-6 border-b">
                <nav className="flex space-x-8">
                    {[{ id: 'orders', name: '외부 주문', count: orders.length }, { id: 'onboarding', name: '온보딩 검토', count: onboardingRequests.filter(o => o.status === 'PENDING').length }, { id: 'organizations', name: '조직 관리', count: organizations.length }].map(tab => (
                        <button key={tab.id} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500 hover:text-slate-700'}`} onClick={() => setActiveTab(tab.id)}>
                            {tab.name}
                            {tab.count > 0 && (<span className="ml-2 bg-brand-navy text.white text-xs px-2 py-1 rounded-full">{tab.count}</span>)}
                        </button>
                    ))}
                </nav>
            </div>

            {/* 에러/리스트 렌더 */}
            {activeTab === 'orders' && (
                <div className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold">외부 주문 목록</h2>
                        <button className="rounded bg-brand-navy text-white px-3 py-2 text-sm" onClick={() => loadData()}>새로고침</button>
                    </div>
                    <ListContainer loading={loading} error={error} onRetry={() => loadData()} header={null} table={OrdersTable} footer={null} />
                </div>
            )}

            {activeTab === 'onboarding' && (
                <div className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold">온보딩 검토</h2>
                    </div>
                    <ListContainer loading={loading} error={error} onRetry={() => loadData()} header={null} table={OnboardingTable} footer={null} />
                </div>
            )}

            {activeTab === 'organizations' && (
                <div className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold">조직 관리</h2>
                        <button className="rounded bg-brand-navy text-white px-3 py-2 text-sm" onClick={() => setShowOrgModal(true)}>새 조직 발급</button>
                    </div>
                    <ListContainer loading={loading} error={error} onRetry={() => loadData()} header={null} table={OrgsTable} footer={null} />
                </div>
            )}

            {/* 온보딩 검토 모달 */}
            {showOnboardingModal && selectedOnboarding && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setShowOnboardingModal(false)}>
                    <div className="w-full max-w-2xl rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">온보딩 검토</h3>
                            <button className="text-slate-500 hover:text-slate-700" onClick={() => setShowOnboardingModal(false)}>✕</button>
                        </div>

                        <div className="space-y-4 text-sm">
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="block text-slate-600 font-medium">신청자</label><div className="mt-1">{selectedOnboarding.applicantName}</div></div>
                                <div><label className="block text-slate-600 font-medium">조직명</label><div className="mt-1">{selectedOnboarding.organizationName}</div></div>
                                <div><label className="block text-slate-600 font-medium">요청 플랜</label><div className="mt-1">{selectedOnboarding.requestedPlan}</div></div>
                                <div><label className="block text-slate-600 font-medium">연락처</label><div className="mt-1">{selectedOnboarding.contactEmail}</div></div>
                            </div>
                            <div><label className="block text-slate-600 font-medium">신청 사유</label><div className="mt-1 p-3 bg-slate-50 rounded">{selectedOnboarding.reason}</div></div>
                            <div><label className="block text-slate-600 font-medium">검토 의견</label><textarea className="mt-1 w-full rounded border px-3 py-2" rows={3} placeholder="검토 의견을 입력하세요" value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} /></div>
                        </div>

                        <div className="mt-6 flex gap-2 justify-end">
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => setShowOnboardingModal(false)}>취소</button>
                            <button className="rounded border border-red-300 text-red-600 px-3 py-2 text-sm hover:bg-red-50" onClick={() => reviewOnboarding(selectedOnboarding.id, false, reviewComment)}>거부</button>
                            <button className="rounded bg-brand-navy text-white px-3 py-2 text-sm" onClick={() => reviewOnboarding(selectedOnboarding.id, true, reviewComment)}>승인</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 조직 발급 모달 */}
            {showOrgModal && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setShowOrgModal(false)}>
                    <div className="w_full max-w-2xl rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">새 조직 발급</h3>
                            <button className="text-slate-500 hover:text-slate-700" onClick={() => setShowOrgModal(false)}>✕</button>
                        </div>
                        <div className="space-y-4 text-sm">
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="block text-slate-600 font-medium">조직명</label><input className="mt-1 w-full rounded border px-3 py-2" value={orgForm.name} onChange={(e) => setOrgForm(prev => ({ ...prev, name: e.target.value }))} placeholder="예: GA 부평1" /></div>
                                <div><label className="block text-slate-600 font-medium">플랜</label><select className="mt-1 w-full rounded border px-3 py-2" value={orgForm.plan} onChange={(e) => setOrgForm(prev => ({ ...prev, plan: e.target.value }))}><option value="BASIC">BASIC</option><option value="STANDARD">STANDARD</option><option value="PREMIUM">PREMIUM</option><option value="ENTERPRISE">ENTERPRISE</option></select></div>
                                <div><label className="block text-slate-600 font-medium">좌석수</label><input type="number" className="mt-1 w-full rounded border px-3 py-2" value={orgForm.seats} onChange={(e) => setOrgForm(prev => ({ ...prev, seats: e.target.value }))} min="1" /></div>
                                <div><label className="block text-slate-600 font-medium">만료일</label><input type="date" className="mt-1 w-full rounded border px-3 py-2" value={orgForm.expiresAt} onChange={(e) => setOrgForm(prev => ({ ...prev, expiresAt: e.target.value }))} /></div>
                                <div><label className="block text-slate-600 font-medium">연락 이메일</label><input type="email" className="mt-1 w-full rounded border px-3 py-2" value={orgForm.contactEmail} onChange={(e) => setOrgForm(prev => ({ ...prev, contactEmail: e.target.value }))} /></div>
                                <div><label className="block text-slate-600 font-medium">연락 전화번호</label><input type="tel" className="mt-1 w-full rounded border px-3 py-2" value={orgForm.contactPhone} onChange={(e) => setOrgForm(prev => ({ ...prev, contactPhone: e.target.value }))} /></div>
                            </div>
                        </div>
                        <div className="mt-6 flex gap-2 justify-end">
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => setShowOrgModal(false)}>취소</button>
                            <button className="rounded bg-brand-navy text-white px-3 py-2 text-sm" onClick={issueOrganization}>발급</button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    )
}


